import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate, useParams } from 'react-router-dom';
import { FiCheckCircle } from 'react-icons/fi';
import signatureService from '../service/signatureService.mjs';
import SignatureCanvas from '../components/SignatureCanvas.mjs';
import PageShell from '../../components/PageShell.mjs';

/**
 * Signing on a phone - /sign/:token from the QR code or a copied link, or
 * /sign to type the short code shown on the computer.
 *
 * Not signed in. The link only lets this page hand one drawing back to the
 * computer that showed it; the signing itself is finished there, by the
 * person signed in. So the page shows a first name and the period, and
 * nothing of the report.
 */

const formatCode = (value) => {
  const raw = String(value || '').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8);
  return raw.length > 4 ? `${raw.slice(0, 4)}-${raw.slice(4)}` : raw;
};

// The sign-in frame: a centred card clear of the fixed navbar.
const Shell = ({ children }) => (
  <PageShell variant="entry">
    <div className="w-full max-w-lg bg-white rounded-xl shadow-xl p-5 sm:p-6">
      {children}
    </div>
  </PageShell>
);

Shell.propTypes = { children: PropTypes.node };

const CodeEntry = () => {
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setProblem('');
    setBusy(true);
    try {
      const token = await signatureService.phoneRedeemCode(code);
      navigate(`/sign/${token}`, { replace: true });
    } catch (err) {
      setProblem(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Shell>
      <h1 className="text-xl font-semibold text-gray-900">Sign on your phone</h1>
      <p className="text-sm text-gray-600 mt-1 text-center">Enter the code shown on your computer.</p>
      <form onSubmit={submit} className="mt-5">
        <label htmlFor="sign-code" className="sr-only">Code</label>
        <input
          id="sign-code"
          value={code}
          onChange={(e) => setCode(formatCode(e.target.value))}
          inputMode="text"
          autoCapitalize="characters"
          autoComplete="one-time-code"
          spellCheck={false}
          placeholder="XXXX-XXXX"
          className="w-full text-center font-mono text-2xl tracking-widest px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-stanford-red"
        />
        {problem && <p className="text-sm text-red-700 mt-3">{problem}</p>}
        <button
          type="submit"
          disabled={busy || code.replace('-', '').length !== 8}
          className="mt-4 w-full min-h-[48px] bg-stanford-red text-white rounded-lg font-semibold disabled:opacity-50"
        >
          {busy ? 'Checking...' : 'Continue'}
        </button>
      </form>
    </Shell>
  );
};

const SignWithToken = ({ token }) => {
  const navigate = useNavigate();
  const padRef = useRef(null);
  const [info, setInfo] = useState(null);
  const [state, setState] = useState('loading'); // loading | ready | sending | sent | dead
  const [hasInk, setHasInk] = useState(false);
  const [problem, setProblem] = useState('');

  useEffect(() => {
    let live = true;
    signatureService.phoneOpen(token)
      .then(data => { if (live) { setInfo(data); setState('ready'); } })
      .catch(err => { if (live) { setProblem(err.message); setState('dead'); } });
    return () => { live = false; };
  }, [token]);

  const send = async () => {
    const image = padRef.current?.toDataURL();
    if (!image) return;
    setProblem('');
    setState('sending');
    try {
      await signatureService.phoneSubmit(token, image);
      setState('sent');
    } catch (err) {
      setProblem(err.message);
      // A link that died while they were drawing cannot be retried.
      setState(err.status === 410 ? 'dead' : 'ready');
    }
  };

  if (state === 'loading') {
    return <Shell><p className="text-sm text-gray-500 text-center py-8">Opening...</p></Shell>;
  }

  if (state === 'dead') {
    return (
      <Shell>
        <h1 className="text-xl font-semibold text-gray-900">This link can&apos;t be used</h1>
        <p className="text-sm text-gray-600 mt-2">
          {problem || 'It has expired or was already used.'} On your computer, choose Phone again to get a new code.
        </p>
        <button
          onClick={() => navigate('/sign', { replace: true })}
          className="mt-5 w-full min-h-[48px] border border-gray-300 text-gray-700 rounded-lg font-medium"
        >
          Enter a new code
        </button>
      </Shell>
    );
  }

  if (state === 'sent') {
    return (
      <Shell>
        <div className="text-center py-4">
          <FiCheckCircle className="w-12 h-12 text-emerald-600 mx-auto" aria-hidden="true" />
          <h1 className="text-xl font-semibold text-gray-900 mt-3">Signature sent</h1>
          <p className="text-sm text-gray-600 mt-2">
            Go back to your computer. Your signature is waiting there - check it and press the button to finish.
          </p>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <h1 className="text-xl font-semibold text-gray-900">
        {info?.firstName ? `Sign, ${info.firstName}` : 'Sign here'}
      </h1>
      <p className="text-sm text-gray-600 mt-1 text-center">
        {info?.report} - {info?.stage}
      </p>

      <div className="relative mt-4 border border-gray-300 rounded-lg bg-gray-50">
        <SignatureCanvas
          ref={padRef}
          height="min(42dvh, 260px)"
          label="Draw your signature"
          onChange={setHasInk}
        />
        <div className="absolute left-4 right-4 bottom-9 border-b border-gray-300 pointer-events-none" />
        <span className="absolute left-4 bottom-3 text-xs text-gray-400 pointer-events-none">
          Sign above the line with your finger
        </span>
      </div>
      <p className="text-xs text-gray-500 mt-2">Turning your phone sideways gives you more room.</p>

      {problem && <p className="text-sm text-red-700 mt-3">{problem}</p>}

      <div className="mt-4 flex gap-3">
        <button
          onClick={() => padRef.current?.clear()}
          disabled={!hasInk || state === 'sending'}
          className="flex-1 min-h-[48px] border border-gray-300 text-gray-700 rounded-lg font-medium disabled:opacity-50"
        >
          Clear
        </button>
        <button
          onClick={send}
          disabled={!hasInk || state === 'sending'}
          className="flex-[2] min-h-[48px] bg-stanford-red text-white rounded-lg font-semibold disabled:opacity-50"
        >
          {state === 'sending' ? 'Sending...' : 'Done'}
        </button>
      </div>
    </Shell>
  );
};

SignWithToken.propTypes = { token: PropTypes.string.isRequired };

const PhoneSignPage = () => {
  const { token } = useParams();
  return token ? <SignWithToken key={token} token={token} /> : <CodeEntry />;
};

export default PhoneSignPage;
